/**
 * IndustryModelPanel — Mission Control surface for assigning an
 * industry profile to an org and (optionally) layering field-level
 * overrides on top.
 *
 * Reads from `/api/industry-profiles` for the global library and
 * `/api/organizations/{org_id}/roi-profile` for the resolved view.
 * The "source map" in the resolved payload powers the badges that
 * show whether each cell came from a global default, the industry
 * profile, or an org override — so admins always know where a number
 * is coming from.
 */
import React, { useState, useEffect, useMemo } from 'react';
import secureStorage from '../../lib/secureStorage';
import authedFetch from '../../lib/authedFetch';
import {
  Loader2, Save, Factory, HeartPulse, Landmark, Cpu, ShoppingBag,
  Briefcase, Building, Zap, Layers, Info, RefreshCcw, Settings2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import logger from '../../lib/logger';

const ICON_MAP = {
  Factory, HeartPulse, Landmark, Cpu, ShoppingBag, Briefcase, Building, Zap,
};

export default function IndustryModelPanel({ orgId }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [resolved, setResolved] = useState(null);
  const [orgOverride, setOrgOverride] = useState(null);
  const [orgIndustryId, setOrgIndustryId] = useState(null);
  const [selectedIndustryId, setSelectedIndustryId] = useState(null);
  const [dirty, setDirty] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const [profilesRes, orgRes] = await Promise.all([
        authedFetch(`${API_URL}/api/industry-profiles`, { headers: { Authorization: `Bearer ${token}` } }),
        authedFetch(`${API_URL}/api/organizations/${orgId}/roi-profile`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (profilesRes.ok) {
        const data = await profilesRes.json();
        setProfiles(data.profiles || []);
      }
      if (orgRes.ok) {
        const data = await orgRes.json();
        setResolved(data.resolved);
        setOrgOverride(data.override || null);
        setOrgIndustryId(data.industry_id_setting || null);
        setSelectedIndustryId(data.resolved?.industry_id || null);
      }
    } catch (e) {
      logger.error('Failed to load industry data', e);
      toast.error('Failed to load industry model');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (orgId) reload(); }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectIndustry = (industryId) => {
    setSelectedIndustryId(industryId);
    // Always allow Save when we have a valid pick — admins might want
    // to pin an explicit override even if it matches the legacy
    // industry_type the resolver inferred. Dirty stays true while
    // they haven't saved yet.
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = secureStorage.getItem('session_token');
      const res = await authedFetch(`${API_URL}/api/organizations/${orgId}/roi-profile/override`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          industry_id: selectedIndustryId,
          // Preserve any existing field-level overrides
          kpi_baselines: orgOverride?.kpi_baselines || [],
          lei_kpi_weights: orgOverride?.lei_kpi_weights || [],
          elasticities: orgOverride?.elasticities || [],
          portfolio_dampening_factor: orgOverride?.portfolio_dampening_factor,
          max_kpi_uplift_percent: orgOverride?.max_kpi_uplift_percent,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setResolved(data.resolved);
        setOrgOverride(data.override);
        setDirty(false);
        toast.success('Industry profile updated for this org');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save industry profile');
      }
    } catch (e) {
      logger.error('Industry profile save failed', e);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClearOverride = async () => {
    if (!window.confirm('Drop all org-level overrides and fall back to the industry profile?')) return;
    setSaving(true);
    try {
      const token = secureStorage.getItem('session_token');
      const res = await authedFetch(`${API_URL}/api/organizations/${orgId}/roi-profile/override`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        // Force state reset before reload so the Clear button hides
        // immediately rather than waiting for the GET round-trip.
        setOrgOverride(null);
        setDirty(false);
        toast.success('Org overrides cleared');
        reload();
      }
    } catch (e) {
      logger.error('Clear override failed', e);
      toast.error('Failed to clear');
    } finally {
      setSaving(false);
    }
  };

  const selectedProfile = useMemo(
    () => profiles.find((p) => p.id === selectedIndustryId),
    [profiles, selectedIndustryId],
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading industry model…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="h-5 w-5 text-indigo-600" /> Industry model
          </CardTitle>
          <CardDescription>
            Pick the industry profile this org should use. The selected profile's KPI baselines,
            LEI→KPI weight matrix, and elasticities replace the global defaults across the entire
            ROI waterfall — Coach narratives, Insights, prospectus exports, and PE simulations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="industry-grid">
            {profiles.map((p) => {
              const Icon = ICON_MAP[p.icon] || Layers;
              const active = p.id === selectedIndustryId;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleSelectIndustry(p.id)}
                  data-testid={`industry-card-${p.id}`}
                  data-active={active}
                  className={`text-left rounded-xl border p-3 transition-all ${
                    active
                      ? 'border-indigo-400 bg-indigo-50 ring-2 ring-indigo-200'
                      : 'border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30'
                  }`}
                >
                  <Icon className={`h-5 w-5 mb-2 ${active ? 'text-indigo-600' : 'text-slate-500'}`} />
                  <div className={`text-sm font-semibold ${active ? 'text-indigo-900' : 'text-slate-800'}`}>
                    {p.name}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">
                    {p.description || p.short_label}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-2">
                    {(p.kpi_priorities || []).slice(0, 3).join(' · ')}
                  </div>
                </button>
              );
            })}
          </div>

          {selectedProfile && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="selected-profile-preview">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-indigo-700">
                    Preview · {selectedProfile.name}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{selectedProfile.description}</div>
                </div>
                {dirty && (
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
                    Unsaved
                  </div>
                )}
              </div>

              {/* KPI priority order */}
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                  Headline KPI priorities
                </Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {(selectedProfile.kpi_priorities || []).map((kid, idx) => (
                    <span
                      key={kid}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white border border-indigo-200 text-indigo-800"
                    >
                      <span className="text-indigo-400">#{idx + 1}</span>
                      {kid.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>

              {/* KPI baseline diffs */}
              {selectedProfile.kpi_baselines?.length > 0 && (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                    KPI baselines that differ from global default ($/employee/yr)
                  </Label>
                  <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-2">
                    {selectedProfile.kpi_baselines.map((kb) => (
                      <div
                        key={kb.kpi_id}
                        className="rounded-lg border border-white bg-white px-3 py-2"
                        title={kb.source || ''}
                      >
                        <div className="text-[10px] uppercase text-slate-400 tracking-wider">{kb.kpi_id.replace(/_/g, ' ')}</div>
                        <div className="text-sm font-bold text-slate-800 font-mono">
                          ${Math.round(kb.baseline_per_employee).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* LLM context */}
              {selectedProfile.llm_industry_context && (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                    AI Coach / Insights tone
                  </Label>
                  <p className="text-xs text-slate-600 italic mt-1 leading-relaxed">
                    “{selectedProfile.llm_industry_context}”
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Source map summary */}
          {resolved && (
            <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600 flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
              <div>
                <div className="font-semibold text-slate-700">Resolved coefficients</div>
                <div className="mt-1">
                  Currently rendering from: <strong className="text-slate-800">{resolved.industry_label || 'Global defaults'}</strong>
                  {orgOverride && Object.keys(orgOverride).filter((k) => !['updated_at', 'updated_by', 'industry_id'].includes(k) && orgOverride[k]).length > 0 && (
                    <> + <strong className="text-amber-700">{Object.keys(orgOverride).filter((k) => !['updated_at', 'updated_by', 'industry_id'].includes(k) && orgOverride[k]).length} org-level override(s)</strong></>
                  )}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  Dampening: <span className="font-mono">{resolved.portfolio_dampening_factor}</span>
                  {' · '}
                  Max KPI uplift: <span className="font-mono">{resolved.max_kpi_uplift_percent}%</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-3 border-t">
            <div className="text-[11px] text-slate-500">
              Field-level overrides (specific KPI baselines or weight cells) can be authored
              by super admins from the global Industry Library — coming in P1.
            </div>
            <div className="flex items-center gap-2">
              {orgOverride && Object.keys(orgOverride || {}).filter((k) => !['updated_at', 'updated_by'].includes(k)).length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleClearOverride}
                  disabled={saving}
                  data-testid="industry-clear-override-btn"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                >
                  <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Clear field-level overrides
                </Button>
              )}
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving || !dirty || !selectedIndustryId}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="industry-save-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Apply industry profile
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
